/**
 * Visualiser shaders.
 *
 * Every mode is a single full-screen fragment shader driven by the same tiny
 * uniform set — time, resolution, band energies and 32 log-spaced spectrum
 * bins — so a mode exists twice: once in GLSL ES 3.0 for the WebGL2 backend
 * and once in WGSL for WebGPU. Both backends draw the identical picture; the
 * renderer picks whichever API the browser can actually give us.
 *
 * Coordinate convention: bottom-left origin, y up, y normalised by height —
 * i.e. GLSL's `gl_FragCoord`. The WGSL path flips y back before running the
 * same math (WebGPU's fragCoord is top-left).
 */

/**
 * Uniform block shared by both backends: 8 scalars + 32 spectrum bins = 160
 * bytes, which is also 16-byte aligned as WGSL's uniform layout requires.
 */
export const FLOATS_PER_FRAME = 40;
/** Spectrum bins uploaded per frame. */
export const LEVEL_FLOATS = 32;

const GLSL_PREAMBLE = `#version 300 es
precision highp float;
uniform float uTime;
uniform vec2 uRes;
uniform float uBass;
uniform float uMid;
uniform float uTreble;
uniform float uLevel;
uniform vec4 uLevels[8];
out vec4 fragColor;

vec3 hue(float h) {
  vec3 p = abs(fract(h + vec3(0.0, 0.6666667, 0.3333333)) * 6.0 - 3.0);
  return clamp(p - 1.0, 0.0, 1.0);
}

/*
 * House palette: metallic gold through signal teal over deep navy, the same
 * colours the station's brand art uses. Modes stay recognisable as themselves
 * but never drift off-brand.
 */
vec3 brand(float t) {
  vec3 gold = vec3(0.98, 0.78, 0.38);
  vec3 teal = vec3(0.30, 0.85, 0.80);
  vec3 deep = vec3(0.10, 0.35, 0.55);
  float k = clamp(t, 0.0, 1.0);
  if (k < 0.5) return mix(gold, teal, k * 2.0);
  return mix(teal, deep, (k - 0.5) * 2.0);
}

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
}

float band(int i) {
  int idx = clamp(i, 0, 31);
  return uLevels[idx >> 2][idx & 3];
}
`;

export const GLSL_VERTEX = `#version 300 es
void main() {
  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}
`;

const GLSL_SYNTHWAVE = `${GLSL_PREAMBLE}void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uRes) / uRes.y;
  float horizon = -0.06;

  float sky = clamp((p.y - horizon) / 0.6, 0.0, 1.0);
  vec3 col = mix(vec3(0.03, 0.10, 0.16), vec3(0.01, 0.02, 0.05), sky);

  vec2 cell = floor(p * 55.0);
  float star = hash(cell);
  float twinkle = 0.5 + 0.5 * sin(uTime * (1.0 + star * 4.0) + star * 20.0);
  if (star > 0.975 && p.y > horizon) {
    col += vec3(0.9, 0.9, 1.0) * twinkle * (0.25 + uTreble * 0.9);
  }

  vec2 sunPos = vec2(0.0, horizon + 0.30 + uBass * 0.05);
  float sunR = 0.24 + uBass * 0.05;
  float d = length(p - sunPos);
  float disc = 1.0 - smoothstep(sunR - 0.01, sunR, d);
  float stripes = step(0.45, fract((p.y - sunPos.y) * (9.0 + uMid * 16.0)));
  float lower = 1.0 - smoothstep(sunPos.y - sunR, sunPos.y + 0.02, p.y);
  float cut = mix(1.0, stripes, lower * (0.5 + 0.5 * uMid));
  float tint = clamp((sunPos.y - p.y) / (sunR * 2.0) + 0.5, 0.0, 1.0);
  vec3 sunCol = mix(vec3(1.0, 0.86, 0.45), vec3(0.72, 0.45, 0.12), tint);
  col = mix(col, sunCol, disc * cut);

  if (p.y < horizon) {
    float depth = horizon - p.y;
    float persp = 1.0 / (depth + 0.02);
    float lineX = smoothstep(0.84, 1.0, abs(fract(p.x * persp * 1.2 + 0.5) - 0.5) * 2.0);
    float scroll = uTime * (0.5 + uBass * 1.8);
    float lineZ = smoothstep(0.84, 1.0, abs(fract(persp * 0.9 - scroll) - 0.5) * 2.0);
    float grid = max(lineX, lineZ) * smoothstep(0.0, 0.2, depth);
    vec3 ground = mix(vec3(0.02, 0.05, 0.09), vec3(0.03, 0.07, 0.12), clamp(depth, 0.0, 1.0));
    col = mix(ground, vec3(0.35, 0.90, 0.85), grid * 0.9);
    col += vec3(1.0, 0.75, 0.30) * exp(-depth * 12.0) * (0.30 + uBass * 0.7);
  }

  col *= 1.0 - 0.30 * dot(p, p);
  col += (hash(gl_FragCoord.xy + fract(uTime)) - 0.5) * 0.035;
  fragColor = vec4(col, 1.0);
}
`;

const GLSL_RINGS = `${GLSL_PREAMBLE}void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uRes) / uRes.y;
  float r = length(p);
  float a = atan(p.y, p.x);
  vec3 col = vec3(0.02, 0.02, 0.05);

  float spin = uTime * 0.15;
  float idxF = floor(((a + spin) / 6.2831853 + 0.5) * 32.0);
  int bi = int(mod(idxF, 32.0));
  float v = band(bi);

  float base = 0.18 + uLevel * 0.05;
  float reach = base + v * 0.45;
  float petal = (1.0 - smoothstep(reach - 0.02, reach, r)) * step(base - 0.03, r);
  col = mix(col, brand(float(bi) / 32.0 + uTime * 0.08) * (0.35 + v), petal * 0.9);

  for (int i = 1; i <= 3; i += 1) {
    float fi = float(i);
    float rr = base + fi * 0.11 + uBass * 0.04 + sin(uTime * (0.6 + fi * 0.2)) * 0.006;
    float ring = 1.0 - smoothstep(0.0, 0.005 + uLevel * 0.005, abs(r - rr));
    col += brand(fract(fi * 0.35 + uTime * 0.03)) * ring * (0.55 - fi * 0.12);
  }

  float core = 1.0 - smoothstep(0.0, base - 0.04 + uBass * 0.05, r);
  col += brand(0.05 + uTime * 0.05) * core * (0.25 + uBass * 0.75);

  col *= 1.0 - 0.28 * dot(p, p);
  col += (hash(gl_FragCoord.xy + fract(uTime)) - 0.5) * 0.035;
  fragColor = vec4(col, 1.0);
}
`;

const GLSL_CRT = `${GLSL_PREAMBLE}void main() {
  vec2 uv = gl_FragCoord.xy / uRes;
  vec2 c = uv - 0.5;
  vec2 duv = clamp(0.5 + c * (1.0 + 0.06 * dot(c, c) * 4.0), 0.001, 0.999);

  float idxF = duv.x * 32.0;
  int bi = int(clamp(idxF, 0.0, 31.0));
  float v = band(bi);
  float gap = smoothstep(0.80, 0.94, fract(idxF));
  float h = 0.03 + v * 0.72;
  float on = step(duv.y, h) * (1.0 - gap);

  vec3 col = vec3(0.01, 0.03, 0.05);
  col += vec3(1.0, 0.76, 0.32) * on * (0.35 + v * 0.9);

  float trace = 1.0 - smoothstep(0.0, 0.015, abs(duv.y - h));
  col += vec3(0.35, 0.90, 0.85) * trace * (0.4 + uTreble * 0.8) * (1.0 - gap);

  float wobble = sin(duv.x * 26.0 + uTime * 2.5) * (0.02 + uLevel * 0.10);
  float wave = 1.0 - smoothstep(0.0, 0.012, abs(duv.y - (0.80 + wobble)));
  col += vec3(1.0, 0.85, 0.30) * wave * (0.25 + uMid * 0.9);

  float scan = 0.78 + 0.22 * sin(gl_FragCoord.y * 1.6 + uTime * 3.0);
  col *= scan;

  float roll = 0.06 * sin(uTime * 0.7);
  col *= 0.75 + 0.45 * (1.0 - smoothstep(0.0, 0.12, abs(duv.y - (0.5 + roll))));

  col *= 1.0 - 1.1 * dot(c, c);
  col += (hash(gl_FragCoord.xy + fract(uTime)) - 0.5) * 0.05;
  fragColor = vec4(max(col, vec3(0.0)), 1.0);
}
`;

export const GLSL_FRAGMENT: Record<string, string> = {
  synthwave: GLSL_SYNTHWAVE,
  rings: GLSL_RINGS,
  crt: GLSL_CRT,
};

// ---------------------------------------------------------------- WGSL

const WGSL_PREAMBLE = `struct Uniforms {
  time: f32,
  resX: f32,
  resY: f32,
  bass: f32,
  mid: f32,
  treble: f32,
  level: f32,
  pad0: f32,
  levels: array<vec4<f32>, 8>,
};

@group(0) @binding(0) var<uniform> U: Uniforms;

struct VSOut {
  @builtin(position) pos: vec4<f32>,
};

fn hue(h: f32) -> vec3<f32> {
  let p = abs(fract(h + vec3<f32>(0.0, 0.6666667, 0.3333333)) * 6.0 - 3.0);
  return clamp(p - 1.0, vec3<f32>(0.0), vec3<f32>(1.0));
}

// House palette: metallic gold through signal teal over deep navy.
fn brand(t: f32) -> vec3<f32> {
  let gold = vec3<f32>(0.98, 0.78, 0.38);
  let teal = vec3<f32>(0.30, 0.85, 0.80);
  let deep = vec3<f32>(0.10, 0.35, 0.55);
  let k = clamp(t, 0.0, 1.0);
  if (k < 0.5) {
    return mix(gold, teal, k * 2.0);
  }
  return mix(teal, deep, (k - 0.5) * 2.0);
}

fn hash2(p: vec2<f32>) -> f32 {
  return fract(sin(dot(p, vec2<f32>(12.9898, 78.233))) * 43758.5453);
}

fn band(i: i32) -> f32 {
  let idx = clamp(i, 0, 31);
  return U.levels[idx >> 2][idx & 3];
}
`;

/** Module-scope declarations shared by a mode's vertex and fragment stage. */
export const WGSL_PREAMBLE_SRC = WGSL_PREAMBLE;

/** Vertex stage, appended after {@link WGSL_PREAMBLE_SRC} in the same module. */
export const WGSL_VERTEX_SRC = `@vertex
fn vs(@builtin(vertex_index) vi: u32) -> VSOut {
  var pts = array<vec2<f32>, 3>(
    vec2<f32>(-1.0, -1.0),
    vec2<f32>(3.0, -1.0),
    vec2<f32>(-1.0, 3.0)
  );
  var out: VSOut;
  out.pos = vec4<f32>(pts[vi], 0.0, 1.0);
  return out;
}
`;

/** Build the complete WGSL module for a mode (preamble + both stages). */
export function wgslModule(mode: string): string {
  const fragment = WGSL_FRAGMENT_BODIES[mode];
  if (!fragment) throw new Error(`unknown visualiser mode: ${mode}`);
  return `${WGSL_PREAMBLE}${WGSL_VERTEX_SRC}${fragment}`;
}

const WGSL_SYNTHWAVE = `const HORIZON: f32 = -0.06;

@fragment
fn fs(@builtin(position) fragCoord: vec4<f32>) -> @location(0) vec4<f32> {
  let fc = vec2<f32>(fragCoord.x, U.resY - fragCoord.y);
  let p = (fc - 0.5 * vec2<f32>(U.resX, U.resY)) / U.resY;
  let horizon = HORIZON;

  let sky = clamp((p.y - horizon) / 0.6, 0.0, 1.0);
  var col = mix(vec3<f32>(0.03, 0.10, 0.16), vec3<f32>(0.01, 0.02, 0.05), sky);

  let cell = floor(p * 55.0);
  let star = hash2(cell);
  let twinkle = 0.5 + 0.5 * sin(U.time * (1.0 + star * 4.0) + star * 20.0);
  if (star > 0.975 && p.y > horizon) {
    col = col + vec3<f32>(0.9, 0.9, 1.0) * twinkle * (0.25 + U.treble * 0.9);
  }

  let sunPos = vec2<f32>(0.0, horizon + 0.30 + U.bass * 0.05);
  let sunR = 0.24 + U.bass * 0.05;
  let d = length(p - sunPos);
  let disc = 1.0 - smoothstep(sunR - 0.01, sunR, d);
  let stripes = step(0.45, fract((p.y - sunPos.y) * (9.0 + U.mid * 16.0)));
  let lower = 1.0 - smoothstep(sunPos.y - sunR, sunPos.y + 0.02, p.y);
  let cut = mix(1.0, stripes, lower * (0.5 + 0.5 * U.mid));
  let tint = clamp((sunPos.y - p.y) / (sunR * 2.0) + 0.5, 0.0, 1.0);
  let sunCol = mix(vec3<f32>(1.0, 0.86, 0.45), vec3<f32>(0.72, 0.45, 0.12), tint);
  col = mix(col, sunCol, disc * cut);

  if (p.y < horizon) {
    let depth = horizon - p.y;
    let persp = 1.0 / (depth + 0.02);
    let lineX = smoothstep(0.84, 1.0, abs(fract(p.x * persp * 1.2 + 0.5) - 0.5) * 2.0);
    let scroll = U.time * (0.5 + U.bass * 1.8);
    let lineZ = smoothstep(0.84, 1.0, abs(fract(persp * 0.9 - scroll) - 0.5) * 2.0);
    let grid = max(lineX, lineZ) * smoothstep(0.0, 0.2, depth);
    let ground = mix(vec3<f32>(0.02, 0.05, 0.09), vec3<f32>(0.03, 0.07, 0.12), clamp(depth, 0.0, 1.0));
    col = mix(ground, vec3<f32>(0.35, 0.90, 0.85), grid * 0.9);
    col = col + vec3<f32>(1.0, 0.75, 0.30) * exp(-depth * 12.0) * (0.30 + U.bass * 0.7);
  }

  col = col * (1.0 - 0.30 * dot(p, p));
  col = col + (hash2(fc + fract(U.time)) - 0.5) * 0.035;
  return vec4<f32>(col, 1.0);
}
`;

const WGSL_RINGS = `@fragment
fn fs(@builtin(position) fragCoord: vec4<f32>) -> @location(0) vec4<f32> {
  let fc = vec2<f32>(fragCoord.x, U.resY - fragCoord.y);
  let p = (fc - 0.5 * vec2<f32>(U.resX, U.resY)) / U.resY;
  let r = length(p);
  let a = atan2(p.y, p.x);
  var col = vec3<f32>(0.02, 0.02, 0.05);

  let spin = U.time * 0.15;
  let idxF = floor(((a + spin) / 6.2831853 + 0.5) * 32.0);
  let bi = i32(idxF - 32.0 * floor(idxF / 32.0));
  let v = band(bi);

  let base = 0.18 + U.level * 0.05;
  let reach = base + v * 0.45;
  let petal = (1.0 - smoothstep(reach - 0.02, reach, r)) * step(base - 0.03, r);
  col = mix(col, brand(f32(bi) / 32.0 + U.time * 0.08) * (0.35 + v), petal * 0.9);

  for (var i: i32 = 1; i <= 3; i = i + 1) {
    let fi = f32(i);
    let rr = base + fi * 0.11 + U.bass * 0.04 + sin(U.time * (0.6 + fi * 0.2)) * 0.006;
    let ring = 1.0 - smoothstep(0.0, 0.005 + U.level * 0.005, abs(r - rr));
    col = col + brand(fract(fi * 0.35 + U.time * 0.03)) * ring * (0.55 - fi * 0.12);
  }

  let core = 1.0 - smoothstep(0.0, base - 0.04 + U.bass * 0.05, r);
  col = col + brand(0.05 + U.time * 0.05) * core * (0.25 + U.bass * 0.75);

  col = col * (1.0 - 0.28 * dot(p, p));
  col = col + (hash2(fc + fract(U.time)) - 0.5) * 0.035;
  return vec4<f32>(col, 1.0);
}
`;

const WGSL_CRT = `@fragment
fn fs(@builtin(position) fragCoord: vec4<f32>) -> @location(0) vec4<f32> {
  let fc = vec2<f32>(fragCoord.x, U.resY - fragCoord.y);
  let uv = fc / vec2<f32>(U.resX, U.resY);
  let c = uv - 0.5;
  let duv = clamp(0.5 + c * (1.0 + 0.06 * dot(c, c) * 4.0), vec2<f32>(0.001), vec2<f32>(0.999));

  let idxF = duv.x * 32.0;
  let bi = i32(clamp(idxF, 0.0, 31.0));
  let v = band(bi);
  let gap = smoothstep(0.80, 0.94, fract(idxF));
  let h = 0.03 + v * 0.72;
  let on = step(duv.y, h) * (1.0 - gap);

  var col = vec3<f32>(0.01, 0.03, 0.05);
  col = col + vec3<f32>(1.0, 0.76, 0.32) * on * (0.35 + v * 0.9);

  let trace = 1.0 - smoothstep(0.0, 0.015, abs(duv.y - h));
  col = col + vec3<f32>(0.35, 0.90, 0.85) * trace * (0.4 + U.treble * 0.8) * (1.0 - gap);

  let wobble = sin(duv.x * 26.0 + U.time * 2.5) * (0.02 + U.level * 0.10);
  let wave = 1.0 - smoothstep(0.0, 0.012, abs(duv.y - (0.80 + wobble)));
  col = col + vec3<f32>(1.0, 0.85, 0.30) * wave * (0.25 + U.mid * 0.9);

  let scan = 0.78 + 0.22 * sin(fc.y * 1.6 + U.time * 3.0);
  col = col * scan;

  let roll = 0.06 * sin(U.time * 0.7);
  col = col * (0.75 + 0.45 * (1.0 - smoothstep(0.0, 0.12, abs(duv.y - (0.5 + roll)))));

  col = col * (1.0 - 1.1 * dot(c, c));
  col = col + (hash2(fc + fract(U.time)) - 0.5) * 0.05;
  return vec4<f32>(max(col, vec3<f32>(0.0)), 1.0);
}
`;

const WGSL_FRAGMENT_BODIES: Record<string, string> = {
  synthwave: WGSL_SYNTHWAVE,
  rings: WGSL_RINGS,
  crt: WGSL_CRT,
};
